/**
 * paperSearchSlice.ts
 * AI 文献检索相关的 Zustand 状态管理切片
 * 包含搜索历史、分析结果、搜索结果、推荐策略等核心业务
 */
import { WrapResponse } from '@/types';
import { StateSlice } from '@/stores/appStore';
import { snakeToCamel } from '@/lib/utils';
import { toast } from 'react-hot-toast';
import { api } from '@/lib/env';
import { fun } from '@/lib/request';
import type {
  SearchStatus,
  SearchHistoryItem,
  AnalysisResult,
  SearchResult,
  RecommendResult,
  ImportToSubjectResponse,
} from '@/components/paper-search/types';

export type PaperSearchSlice = {
  // 状态
  searchHistory: SearchHistoryItem[];
  currentQuery: string;
  analysisResult: AnalysisResult | null;
  searchResult: SearchResult | null;
  recommendResult: RecommendResult | null;
  filter: string;
  searchStatus: SearchStatus;
  loading: {
    history: boolean;
    analysis: boolean;
    search: boolean;
    recommend: boolean;
  };

  // UI 打字效果状态（用于 ui_analysis_text）
  typingText: string; // 当前显示的打字文本
  typingCompleted: boolean; // 打字是否完成

  // 阶段1打字效果完成状态（用于控制阶段2的启动时机）
  analysisTypingCompleted: boolean; // 阶段1分析文本打字效果是否完成
  analysisTypingCompletedPromise: Promise<void> | null; // 阶段1打字效果完成的 Promise
  resolveAnalysisTypingCompleted: (() => void) | null; // 阶段1打字效果完成的 resolver

  // 阶段3打字效果完成状态（用于控制最终状态的设置时机）
  thoughtTypingCompleted: boolean; // 阶段3思考文本打字效果是否完成

  // 历史记录加载标志
  isLoadingHistory: boolean; // 是否正在加载历史记录

  // API 方法（所有 API 调用都在这里实现）
  fetchSearchHistory: () => Promise<void>;
  deleteSearchHistory: (historyId: string) => Promise<void>;
  analysisQuery: (query: string) => Promise<void>;
  searchWorks: (query: string, filter: string, analysisResult: AnalysisResult) => Promise<void>;
  searchResultRecommend: (searchId: string) => Promise<void>;
  importToSubject: (searchId: string, paperIndexes: number[]) => Promise<string[] | null>;

  // 状态管理方法
  setFilter: (filter: string) => void;
  clearSearch: () => void;
  setTypingText: (text: string) => void;
  setTypingCompleted: (completed: boolean) => void;
  setAnalysisTypingCompleted: (completed: boolean) => void;
  setThoughtTypingCompleted: (completed: boolean) => void;
  waitForAnalysisTypingCompleted: () => Promise<void>; // 等待阶段1打字效果完成的 Promise
  loadHistoryItem: (item: SearchHistoryItem) => void;
};

export const createPaperSearchSlice: StateSlice<PaperSearchSlice> = (
  set,
  get
) => ({
  // 初始状态
  searchHistory: [],
  currentQuery: '',
  analysisResult: null,
  searchResult: null,
  recommendResult: null,
  filter: '',
  searchStatus: 'idle',
  loading: {
    history: false,
    analysis: false,
    search: false,
    recommend: false,
  },
  typingText: '',
  typingCompleted: false,
  analysisTypingCompleted: false,
  analysisTypingCompletedPromise: null,
  resolveAnalysisTypingCompleted: null,
  thoughtTypingCompleted: false,
  isLoadingHistory: false,

  // API 方法
  fetchSearchHistory: async () => {
    set((state) => {
      state.loading.history = true;
    });
    try {
      const { data } = await fun.post<WrapResponse<SearchHistoryItem[]>>(
        api('/paper/search/history', true),
        {}
      );
      if (data.code === 0) {
        const historyItems = snakeToCamel<SearchHistoryItem[]>(data.data);
        // 为每个历史记录的 searchResult 添加 paperIndex（从 0 开始）
        const historyItemsWithIndex = historyItems.map((item) => ({
          ...item,
          searchResult: item.searchResult?.map((paper, index) => ({
            ...paper,
            paperIndex: index,
          })) || [],
        }));
        set((state) => {
          state.searchHistory = historyItemsWithIndex;
        });
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error fetching search history:', error);
      toast.error('获取搜索历史失败');
    } finally {
      set((state) => {
        state.loading.history = false;
      });
    }
  },

  deleteSearchHistory: async (historyId: string) => {
    try {
      const { data } = await fun.post<WrapResponse<{}>>(
        api('/paper/search/delHistory', true),
        { history_id: historyId }
      );
      if (data.code === 0) {
        // 从列表中移除
        set((state) => {
          state.searchHistory = state.searchHistory.filter(
            (item) => item.historyId !== historyId
          );
        });
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error deleting search history:', error);
      toast.error('删除搜索历史失败');
    }
  },

  analysisQuery: async (query: string) => {
    set((state) => {
      state.loading.analysis = true;
      state.searchStatus = 'analyzing';
      state.currentQuery = query;
      state.typingText = '';
      state.typingCompleted = false;
      state.analysisTypingCompleted = false; // 重置阶段1打字效果完成状态
      state.analysisTypingCompletedPromise = null; // 重置 Promise
      state.resolveAnalysisTypingCompleted = null; // 重置 resolver
      state.thoughtTypingCompleted = false; // 重置阶段3打字效果完成状态
      state.isLoadingHistory = false;
      // 清空之前的搜索结果
      state.searchResult = null;
      state.recommendResult = null;
    });
    try {
      const { data } = await fun.post<WrapResponse<AnalysisResult>>(
        api('/paper/search/analysisQuery', true),
        { query }
      );
      if (data.code === 0) {
        const analysisResult = snakeToCamel<AnalysisResult>(data.data);
        set((state) => {
          state.analysisResult = analysisResult;
        });
      } else {
        toast.error(data.message);
        set((state) => {
          state.searchStatus = 'idle';
        });
      }
    } catch (error) {
      console.error('Error analyzing query:', error);
      toast.error('分析搜索词失败');
      set((state) => {
        state.searchStatus = 'idle';
      });
    } finally {
      set((state) => {
        state.loading.analysis = false;
      });
    }
  },

  searchWorks: async (query: string, filter: string, analysisResult: AnalysisResult) => {
    set((state) => {
      state.loading.search = true;
      state.searchStatus = 'searching';
      // 注意：不更新 state.filter，因为 filter 已经在 executeSearch 中保存了原始的带标记版本
      // 这里传入的 filter 是清理后的版本（用于 API 调用），不应该覆盖 store 中的原始 filter
    });
    try {
      // 将 analysisResult 从 camelCase 转换为 snake_case
      const analysisResultSnake = snakeToCamel<AnalysisResult, false>(
        analysisResult,
        { reverse: true }
      );
      const { data } = await fun.post<WrapResponse<SearchResult>>(
        api('/paper/search/works', true),
        {
          query,
          filter,
          analysis_result: analysisResultSnake
        }
      );
      if (data.code === 0) {
        const searchResult = snakeToCamel<SearchResult>(data.data);
        // 为每个 paper 添加 paperIndex（从 0 开始）
        if (searchResult.result && searchResult.result.length > 0) {
          searchResult.result = searchResult.result.map((paper, index) => ({
            ...paper,
            paperIndex: index,
          }));
        }
        set((state) => {
          state.searchResult = searchResult;
          // 如果没有结果，设置为 no-results
          if (searchResult.matchCount === 0 || !searchResult.result?.length) {
            state.searchStatus = 'no-results';
          }
        });
      } else {
        toast.error(data.message);
        set((state) => {
          state.searchStatus = 'idle';
        });
      }
    } catch (error) {
      console.error('Error searching works:', error);
      toast.error('搜索文献失败');
      set((state) => {
        state.searchStatus = 'idle';
      });
    } finally {
      set((state) => {
        state.loading.search = false;
      });
    }
  },

  searchResultRecommend: async (searchId: string) => {
    set((state) => {
      state.loading.recommend = true;
      state.searchStatus = 'reading';
    });
    try {
      const { data } = await fun.post<WrapResponse<RecommendResult>>(
        api('/paper/search/searchResultRecommend', true),
        { search_id: searchId }
      );
      if (data.code === 0) {
        const recommendResult = snakeToCamel<RecommendResult>(data.data);
        set((state) => {
          state.recommendResult = recommendResult;
          // 注意：不立即设置 searchStatus = 'completed'
          // 需要等待阶段3的打字效果完成后才设置（在 AIAssistantPanel 中处理）
        });
      } else {
        toast.error(data.message);
        set((state) => {
          state.searchStatus = 'completed';
        });
      }
    } catch (error) {
      console.error('Error fetching recommend result:', error);
      toast.error('获取推荐策略失败');
      set((state) => {
        state.searchStatus = 'completed';
      });
    } finally {
      set((state) => {
        state.loading.recommend = false;
      });
    }
  },

  importToSubject: async (searchId: string, paperIndexes: number[]) => {
    try {
      const { data } = await fun.post<WrapResponse<ImportToSubjectResponse>>(
        api('/paper/search/importToSubject', true),
        {
          search_id: searchId,
          paper_index: paperIndexes,
        }
      );
      if (data.code === 0) {
        // 使用 snakeToCamel 转换响应数据（参考 RecommendResult 的处理方式）
        // 先转换对象结构，然后手动处理数组中的 id string 化
        const result = snakeToCamel<ImportToSubjectResponse>(
          data.data
        );
        // 将数组中的每个 id 转换为 string（参考 paperSlice 中 modifier 的处理方式）
        const paperIds = result?.paperIds?.map((id) => id.toString()) || [];
        return paperIds.length > 0 ? paperIds : null;
      } else {
        toast.error(data.message || '导入文献失败');
        return null;
      }
    } catch (error) {
      console.error('Error importing papers:', error);
      toast.error('导入文献失败，请稍后重试');
      return null;
    }
  },

  // 状态管理方法
  setFilter: (filter: string) => {
    set((state) => {
      state.filter = filter;
    });
  },

  clearSearch: () => {
    set((state) => {
      state.currentQuery = '';
      state.analysisResult = null;
      state.searchResult = null;
      state.recommendResult = null;
      state.filter = '';
      state.searchStatus = 'idle';
      state.typingText = '';
      state.typingCompleted = false;
      state.analysisTypingCompleted = false;
      state.analysisTypingCompletedPromise = null;
      state.resolveAnalysisTypingCompleted = null;
      state.thoughtTypingCompleted = false;
    });
  },

  setTypingText: (text: string) => {
    set((state) => {
      state.typingText = text;
    });
  },

  setTypingCompleted: (completed: boolean) => {
    set((state) => {
      state.typingCompleted = completed;
    });
  },

  setAnalysisTypingCompleted: (completed: boolean) => {
    set((state) => {
      state.analysisTypingCompleted = completed;
      // 如果完成，resolve Promise
      if (completed && state.resolveAnalysisTypingCompleted) {
        state.resolveAnalysisTypingCompleted();
        state.resolveAnalysisTypingCompleted = null;
        state.analysisTypingCompletedPromise = null;
      }
    });
  },

  waitForAnalysisTypingCompleted: () => {
    const state = get();
    // 如果已经完成，立即返回 resolved Promise
    if (state.analysisTypingCompleted) {
      return Promise.resolve();
    }
    // 如果已经有 Promise，返回现有的 Promise
    if (state.analysisTypingCompletedPromise) {
      return state.analysisTypingCompletedPromise;
    }
    // 创建新的 Promise
    let promiseResolver: (() => void) | null = null;
    const promise = new Promise<void>((resolve) => {
      promiseResolver = resolve;
    });
    // 在 Promise 创建完成后，再设置到 state
    set((state) => {
      state.analysisTypingCompletedPromise = promise;
      state.resolveAnalysisTypingCompleted = promiseResolver!;
    });
    return promise;
  },

  setThoughtTypingCompleted: (completed: boolean) => {
    set((state) => {
      state.thoughtTypingCompleted = completed;
    });
  },

  // 直接加载历史记录数据（跳过API调用和打字效果）
  loadHistoryItem: (item: SearchHistoryItem) => {
    // 为历史记录的 searchResult 添加 paperIndex（从 0 开始）
    const searchResultWithIndex = item.searchResult?.map((paper, index) => ({
      ...paper,
      paperIndex: index,
    })) || [];

    set((state) => {
      state.isLoadingHistory = true;
      state.currentQuery = item.query;
      state.analysisResult = item.analysisResult;
      // 将 SearchResultPaper[] 转换为 SearchResult 格式
      // 注意：history_id 就是 search_id，直接使用 historyId 作为 searchId
      state.searchResult = {
        searchId: item.historyId, // history_id 就是 search_id
        matchCount: searchResultWithIndex.length,
        result: searchResultWithIndex,
      };
      state.recommendResult = item.recommendResult || null;
      // 直接设置完整的打字文本，跳过打字效果
      state.typingText = item.analysisResult?.uiAnalysisText || '';
      state.typingCompleted = true;
      state.analysisTypingCompleted = true; // 历史记录加载时，阶段1打字效果视为已完成
      // 如果 Promise 存在，立即 resolve
      if (state.resolveAnalysisTypingCompleted) {
        state.resolveAnalysisTypingCompleted();
        state.resolveAnalysisTypingCompleted = null;
        state.analysisTypingCompletedPromise = null;
      }
      state.thoughtTypingCompleted = true; // 历史记录加载时，阶段3打字效果视为已完成
      // 根据是否有结果设置状态
      if (item.searchResult && item.searchResult.length > 0) {
        state.searchStatus = item.recommendResult ? 'completed' : 'no-results';
      } else {
        state.searchStatus = 'no-results';
      }
      // 不设置任何 loading 状态，保持为 false
    });
  },
});
